import {
  Badge,
  Button,
  Card,
  Container,
  ErrorState,
  NoPermissionState,
  Notice,
  PageHeader,
  Skeleton,
  Toolbar,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Download, Printer, Receipt, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import {
  BillingDocumentPreview,
  BillingDocumentScroller,
} from '@/features/billing/BillingDocumentPreview';
import { creditNotePreviewProps } from '@/features/billing/preview';
import {
  billingKeys,
  billingSettingsQueryOptions,
  creditNotePdf,
  creditNoteQueryOptions,
  issueCreditNote,
} from '@/features/billing/queries';
import { creditNoteActions, creditNoteTone } from '@/features/billing/status';
import { openDownloadedFile } from '@/lib/api';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount } from '@/lib/format';

export const Route = createFileRoute('/_app/billing/credit-notes/$creditNoteId')({
  component: CreditNoteDetailPage,
});

function CreditNoteDetailPage() {
  const { creditNoteId } = Route.useParams();
  const { t, i18n } = useTranslation(['billing', 'common', 'errors']);
  const session = useSession();
  const queryClient = useQueryClient();
  const canRead = session.can('invoice.read');

  const query = useQuery(creditNoteQueryOptions(creditNoteId, canRead));
  const settings = useQuery(billingSettingsQueryOptions(canRead));

  const openPdf = useMutation({
    mutationFn: () => creditNotePdf(creditNoteId),
    onSuccess: (file) => {
      openDownloadedFile(file);
      toast.success(t('billing:toasts.pdfOpened'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  /**
   * Émission d'un avoir resté au brouillon : le tiroir enchaîne normalement
   * création puis émission, mais un échec entre les deux appels laisse un
   * `DRAFT` — c'est ici qu'on le termine (il n'a alors consommé aucun numéro).
   */
  const issue = useMutation({
    mutationFn: () => issueCreditNote(creditNoteId, query.data?.version ?? 0),
    onSuccess: async (note) => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all });
      toast.success(t('billing:toasts.creditNoteIssued', { number: note.number ?? '' }));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  if (query.isPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton variant="title" />
          <Skeleton height={480} />
        </div>
      </Container>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Container>
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const note = query.data;
  const actions = creditNoteActions(note.status, session.can);

  return (
    <Container>
      <PageHeader
        title={note.number ?? t('billing:status.creditNote.DRAFT')}
        subtitle={t('billing:creditNotes.detailSubtitle', {
          number: note.number ?? '—',
          invoice: note.invoiceNumber ?? '—',
        })}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          {
            label: t('billing:creditNotes.title'),
            link: <Link to="/billing/credit-notes">{t('billing:creditNotes.title')}</Link>,
          },
          { label: note.number ?? t('billing:status.creditNote.DRAFT') },
        ]}
        actions={
          <Badge tone={creditNoteTone(note.status)} size="lg" dot>
            {t(`billing:status.creditNote.${note.status}`)}
          </Badge>
        }
      />

      <Card className="mb-4 no-print">
        <Toolbar style={{ borderBottom: 0, borderRadius: 'var(--r-md)' }}>
          <span className="strong display t-16">
            −{formatAmount(note.totals.netTotal, i18n.language)}
          </span>
          <span className="spacer" />
          <Button variant="ghost" size="sm" onClick={() => window.print()}>
            <Printer className="ico ico-14" aria-hidden="true" />
            {t('billing:document.print')}
          </Button>
          {actions.downloadPdf ? (
            <Button
              variant="secondary"
              size="sm"
              loading={openPdf.isPending}
              onClick={() => openPdf.mutate()}
            >
              <Download className="ico ico-14" aria-hidden="true" />
              {t('billing:document.downloadPdf')}
            </Button>
          ) : null}
          {actions.issue ? (
            <Button
              variant="danger"
              size="sm"
              loading={issue.isPending}
              onClick={() => issue.mutate()}
            >
              <Send className="ico ico-14" aria-hidden="true" />
              {t('billing:creditNotes.issueAction')}
            </Button>
          ) : null}
          <Button variant="primary" size="sm" asChild>
            <Link to="/billing/invoices/$invoiceId" params={{ invoiceId: note.invoiceId }}>
              <Receipt className="ico ico-14" aria-hidden="true" />
              {t('billing:creditNotes.columns.invoice')}
            </Link>
          </Button>
        </Toolbar>
      </Card>

      {note.status === 'DRAFT' ? (
        <Notice tone="warning" className="mb-4 no-print">
          {t('billing:creditNotes.draftNotice')}
        </Notice>
      ) : null}

      <Notice tone="info" className="mb-4 no-print">
        {note.reason}
      </Notice>

      <BillingDocumentScroller>
        <BillingDocumentPreview {...creditNotePreviewProps(note, settings.data)} />
      </BillingDocumentScroller>
    </Container>
  );
}
